/**
 * @module ConstructorIO/helpers
 */

import loadMoreButton from '../../spa/components/search/load-more-button';
import productGrid from '../../spa/components/search/product-grid';
import productResults from '../../spa/components/search/product/product-results';
import refinementResults from '../../spa/components/search/refinements/refinement-results';
import refinementUrlBuilder from '../search/refinement-url-builder';
import enhanceProduct from './enhanceProduct';
import trackEccomerceImpression from './tracking';
import {decodeValue} from './url';

/**
 * @param {import('../../spa/type-definitions').SearchResult} result - Constructor search result
 */
const renderRefinements = result => {
    const wrapper = document.querySelector('#wrapper');
    const secondary = wrapper.querySelector('#secondary');

    if (secondary.dataset.seoUrlConfig) {
        result.seoUrlConfig = JSON.parse(secondary.dataset.seoUrlConfig);
        result.shouldIndexPage = secondary.dataset.shouldBeIndexed;
    }

    const refinementsHTML = refinementResults(result);
    const contentContainer = secondary.querySelector('.inner-wrapper-sticky') || secondary;
    const elementsToReAttach = [...contentContainer.children].filter(
        child => child.id !== 'refinements-filters' && child.id !== 'refinements-apply'
    );
    elementsToReAttach.forEach(element => {
        element.remove();
    });

    secondary.innerHTML = refinementsHTML;
    elementsToReAttach.forEach(element => {
        secondary.appendChild(element);
    });
    const canonicalElement = document.querySelector('link[rel="canonical"]');
    if (result && result.page === 1 && canonicalElement && canonicalElement.href) {
        let canonicalLink = '';

        if (result.seoUrlConfig && result.seoUrlConfig.length > 0 && result.shouldIndexPage) {
            canonicalLink = canonicalElement.href;
        } else {
            [canonicalLink] = canonicalElement.href.split('?');
        }

        canonicalElement.href = canonicalLink;
    }
};

/**
 * @param {import('../../spa/type-definitions').SearchResult} result - Constructor search result
 */
const renderGrid = result => {
    const wrapper = document.querySelector('#wrapper');
    const primary = wrapper.querySelector('#primary');
    const skeleton = wrapper.querySelector('#skeleton');
    const productGridHTML = productGrid(result);

    if (result && skeleton) {
        skeleton.remove();
    }

    primary.innerHTML = productGridHTML;

    enhanceProduct.afterRender();

    if (result.isCosmeticsPage) {
        return;
    }
    renderRefinements(result);
};

/**
 * @param {HTMLElement} buttonContainer - clicked button parent container
 * @param {import('../../spa/type-definitions').SearchResult} result - Constructor search result
 */
const renderTiles = (buttonContainer, result) => {
    const searchResults = document.querySelector('#search-result-items');
    const productResultsHTML = productResults(result);
    searchResults.insertAdjacentHTML('beforeend', productResultsHTML);
    enhanceProduct.afterRender();
    if (result.nextPage !== null) {
        const loadMoreHTML = loadMoreButton(result);
        if (loadMoreHTML !== '') {
            const parser = new DOMParser();
            const content = parser.parseFromString(loadMoreHTML, 'text/html');
            buttonContainer.replaceWith(content.querySelector('.show-more-products-button'));
        }
    } else {
        buttonContainer.remove();
    }
};

/**
 * Build additional parameters that will not be part of the page URL
 *
 * @param {Object} parameters - parameters to include in the search
 * @param {boolean} isPreOrderCategory - whether the browsed category is of type "pre-order"
 * @returns {Object|null} final request parameters or `null`
 */
const buildFinalRequestParameters = (parameters, isPreOrderCategory) => {
    let reqParams = {...parameters};
    let implicitParams = {};
    if (isPreOrderCategory) {
        implicitParams = {
            filters: {
                preOrder: 'True'
            }
        };
    }
    if (reqParams && reqParams.filters) {
        for (const [id, value] of Object.entries(parameters.filters)) {
            const processedValue = decodeValue(value);
            parameters.filters[id] = processedValue;
        }
        reqParams = {...parameters, ...implicitParams}; // outer copy
        for (const p in reqParams) {
            if (Object.prototype.hasOwnProperty.call(reqParams, p)) {
                reqParams[p] = {...parameters[p], ...implicitParams[p]}; // copy nested values
            }
        }
    } else if (Object.keys(implicitParams).length > 0) {
        reqParams = implicitParams;
    }

    return reqParams;
};

/**
 * @param {import('../search').default} constructorIOSearch - Constructor.io client wrapper
 * @param {Object} [parameters] - optional set of implicit parameters that will not be part of the page URL
 * @returns {Promise<import('../../spa/type-definitions').SearchResult>} result
 */
const searchProducts = async (constructorIOSearch, parameters) => {
    const wrapper = document.querySelector('#wrapper');
    const {categoryId, searchPhrase, isPreOrderCategory} = wrapper.dataset;
    let result = null;
    if (constructorIOSearch.isEnabled && (categoryId || searchPhrase)) {
        const reqParams = buildFinalRequestParameters(parameters, isPreOrderCategory === 'true');
        if (categoryId !== '') {
            result = await constructorIOSearch.browse(categoryId, reqParams);
        } else {
            result = await constructorIOSearch.search(searchPhrase, reqParams);
        }
    }
    if (SitePreferences.ENABLE_WISHLIST_CLP_AND_REC && result && result.products) {
        const $wishlistLink = $('li.nav-item-wishlist a.mini-wishlist-link');
        const productsInWishlist = $wishlistLink.data('favpids').toString().split('|');

        if (productsInWishlist.length > 0) {
            result.products.forEach((product, index, productArray) => {
                if (productsInWishlist.includes(product.ID)) {
                    productArray[index].isInWishlist = true;
                } else if (product.variationsMap) {
                    productArray[index].isInWishlist = product.variationsMap.some(variationId => {
                        return productsInWishlist.includes(variationId);
                    });
                } else {
                    productArray[index].isInWishlist = false;
                }
            });
        }
    }

    result = await enhanceProduct.enhance(result);
    return result;
};

/**
 * @param {Object} filters - object with selected filters
 * @param {string} id - selected refinement ID
 * @param {string} value - selected refinement value
 */
const removeSelectedRefinementFromObject = (filters, id, value) => {
    let values = filters[id];
    if (!values) {
        return;
    }
    if (typeof values === 'string') {
        values = values.split(',');
    }
    values = values.filter(v => v !== value).join(',');
    if (values !== '') {
        filters[id] = values;
    } else {
        delete filters[id];
    }
};

/**
 * @param {import('../search').default} constructorIOSearch - Constructor.io client wrapper
 * @param {HTMLButtonElement} loadMoreBtn - load more button that was clicked
 * @returns {Promise<boolean>} - whether more products were loaded successfully
 */
export const loadMoreProducts = async (constructorIOSearch, loadMoreBtn) => {
    const {nextPageUrl} = loadMoreBtn.dataset;
    window.history.pushState(window.history.state, document.title, nextPageUrl.toString());
    const result = await searchProducts(constructorIOSearch);
    if (result !== null && result.numTotalResults !== 0) {
        const canonicalElement = document.querySelector('link[rel="canonical"]');
        if (canonicalElement && canonicalElement.href) {
            const canonicalLink = canonicalElement.href.split('?')[0];
            canonicalElement.href = `${canonicalLink}?page=${result.page}`;
        }

        const buttonContainer = loadMoreBtn.parentElement;
        renderTiles(buttonContainer, result);
        return true;
    }
    return false;
};

/**
 * @param {Object} [refinements] - additional filters to apply
 * @param {string} [refineURL] - or refinement URL to apply
 */
const handleApplyButton = (refinements, refineURL) => {
    let nextPageURL = null;
    if (refinements && Object.keys(refinements).length > 0) {
        nextPageURL = new URL(refinementUrlBuilder.getMultipleRefinementsValuesURL(refinements, refineURL));
        nextPageURL.searchParams.delete('page');
    } else {
        nextPageURL = new URL(refineURL);
        nextPageURL.searchParams.delete('page');
    }
    window.history.pushState(window.history.state, document.title, nextPageURL.toString());
};

/**
 * @param {HTMLElement} wrapper - site wrapper
 */
const handleNoHits = async wrapper => {
    const {searchPhrase, categoryId} = wrapper.dataset;
    const noHitsURL = new URL(window.Urls.SEARCH_NO_HITS);
    if (searchPhrase) {
        noHitsURL.searchParams.set('q', searchPhrase);
    } else if (categoryId) {
        noHitsURL.searchParams.set('cgid', categoryId);
    }
    window.location = noHitsURL;
};

/**
 * @param {import('../search').default} constructorIOSearch - Constructor search wrapper
 * @param {HTMLElement} [applyButton] - the apply filters button if clicked
 * @param {Object} [additionalFilters] - object with additional filters to apply
 * @param {string} [refineURL] - url with already applied refinements to use
 * @returns {number} number of total results
 */
export const updateProductGrid = async (constructorIOSearch, applyButton, additionalFilters, refineURL) => {
    if (applyButton && applyButton.id === 'refinements-apply') {
        handleApplyButton(additionalFilters, refineURL);
    } else if (refineURL) {
        window.history.pushState(window.history.state, document.title, refineURL);
    }
    const result = await searchProducts(constructorIOSearch);
    if (result !== null && result.numTotalResults !== 0) {
        renderGrid(result);
        return result.numTotalResults;
    }

    return 0;
};

/**
 * @param {import('../search').default} constructorIOSearch - Constructor.io client wrapper
 * @returns {Promise<void>}
 */
export const initialRender = async constructorIOSearch => {
    const wrapper = document.querySelector('#wrapper');
    if (constructorIOSearch.isEnabled) {
        const result = await searchProducts(constructorIOSearch);
        if (result !== null && result.numTotalResults !== 0) {
            renderGrid(result);

            const updateEvent = new Event('plp.updateproductlisting', {bubbles: true, cancelable: false});
            window.dispatchEvent(updateEvent);

            const {categoryId, searchPhrase} = wrapper.dataset;

            let list = '';
            let pageType = '';

            if (categoryId) {
                list = categoryId;
                pageType = 'MainCategory';
            } else if (searchPhrase) {
                list = `search_${searchPhrase}`;
                pageType = 'search';
            }

            await trackEccomerceImpression(list, pageType, result.products);
        } else if (window.Urls?.SEARCH_NO_HITS) {
            await handleNoHits(wrapper);
        }
    }
};

/**
 * Updates only the refinements section of the page.
 * Used for mobile where the filters can be selected without being applied.
 * Updates the refinements UI without updating the search results.
 *
 * @param {import('../search').default} constructorIOSearch - Constructor.io client wrapper
 * @param {HTMLAnchorElement} [clickedFilter] - optionally clicked refinement to extract data from
 * @param {Object} [additionalFilters] - optional filters to include in the search
 * @param {string} [refineURL] - optional url with refinements to render the results based on
 * @returns {number} number of total results
 */
export const updateRefinementSelection = async (constructorIOSearch, clickedFilter, additionalFilters, refineURL) => {
    const additionalParameters = {
        options: {
            nonInstantUpdate: true // on mobile when selecting refinement we don't reload the page, just changing info in refinement window
        }
    };
    if (clickedFilter) {
        const {refinementId, refinementValue, isSelected} = clickedFilter.dataset;

        if (additionalFilters && Object.keys(additionalFilters).length > 0) {
            additionalParameters.filters = {...additionalFilters};
            if (isSelected === 'true') {
                removeSelectedRefinementFromObject(additionalParameters.filters, refinementId, refinementValue);
                if (Object.keys(additionalParameters.filters).length === 0) {
                    delete additionalParameters.filters;
                }
            }
        }
    } else if (refineURL) {
        window.history.pushState(window.history.state, document.title, refineURL);
        additionalParameters.options.nonInstantUpdate = false;
    }

    const result = await searchProducts(constructorIOSearch, additionalParameters);
    if (result !== null && !result.isCosmeticsPage) {
        renderRefinements(result);
        return result.numTotalResults;
    }
    return 0;
};

/**
 * @returns {boolean} whether the search is refined
 */
export const isRefinedSearch = () => {
    const {search} = new URL(window.location.href);
    return search.includes('filters');
};
